// src/theme/focusVisible.ts
var FOCUS_RING_WIDTH_PX = 3;
function focusRingColors(mode) {
  return {
    onSurface: mode === "dark" ? "#ffffff" : "#000000",
    onFill: "#000000"
  };
}
function focusRing(color) {
  return {
    outlineWidth: `${FOCUS_RING_WIDTH_PX}px`,
    outlineStyle: "solid",
    outlineColor: color,
    // Inward, so the ring is measured against the control's own surface rather
    // than whatever page background sits behind it.
    outlineOffset: `-${FOCUS_RING_WIDTH_PX}px`
  };
}
function focusVisibleComponents(mode, palette) {
  const modeInks = focusRingColors(mode);
  const decisions = palette ? focusRingInkDecisions(mode, palette) : void 0;
  const onSurface = decisions ? decisions.surface.ink : modeInks.onSurface;
  const onFill = modeInks.onFill;
  const switched = decisions ? RING_PALETTE_SLOTS.filter((slot) => decisions.fills[slot].switched) : [];
  const perColour = (selector) => Object.fromEntries(
    switched.map((slot) => [selector(capitalise(slot)), { outlineColor: decisions.fills[slot].ink }])
  );
  const onPaper = () => ({
    styleOverrides: { root: { "&.Mui-focusVisible": focusRing(onSurface) } }
  });
  return {
    MuiButton: {
      styleOverrides: {
        root: {
          "&.Mui-focusVisible": focusRing(onSurface),
          // `(0,3,0)` beats the rule above by specificity rather than relying on
          // the order MUI's overridesResolver walks the style slots in.
          "&.MuiButton-contained.Mui-focusVisible": { outlineColor: onFill },
          // The `.MuiButton-root` prefix lifts these to `(0,5,0)` so they win
          // outright instead of depending on emotion's emit order.
          ...perColour(
            (slot) => `&.MuiButton-root.MuiButton-contained.MuiButton-color${slot}.Mui-focusVisible`
          )
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // `IconButton` paints no background in any colour, so there is no
          // filled case to split off.
          "&.Mui-focusVisible": focusRing(onSurface)
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          // MUI hard-codes a default `Fab` to `grey[300]`, a light surface even
          // in dark mode, so it starts from the fill ink.
          "&.Mui-focusVisible": focusRing(onFill),
          ...perColour((slot) => `&.MuiFab-root.MuiFab-${slot.toLowerCase()}.Mui-focusVisible`)
        }
      }
    },
    // Unselected and selected alike, because `Mui-selected` is an
    // `action.selected` alpha over the paper rather than a fill.
    MuiToggleButton: onPaper(),
    MuiTab: onPaper(),
    MuiMenuItem: onPaper(),
    MuiListItemButton: onPaper(),
    MuiCardActionArea: onPaper(),
    MuiChip: {
      styleOverrides: {
        root: {
          // Default and outlined chips are an `action` alpha over the paper.
          "&.Mui-focusVisible": focusRing(onSurface),
          // MUI paints a focused filled coloured chip with `palette.X.dark`, so
          // the surface under the ring is the hover fill with no pointer involved.
          "&.MuiChip-filled:not(.MuiChip-colorDefault).Mui-focusVisible": { outlineColor: onFill },
          ...perColour(
            (slot) => `&.MuiChip-root.MuiChip-filled.MuiChip-color${slot}.Mui-focusVisible`
          )
        }
      }
    }
  };
}
var FOCUS_RING_CONTRAST_FLOOR = 3;
var RING_INK_DARK = "#000000";
var RING_INK_LIGHT = "#ffffff";
var RING_PALETTE_SLOTS = ["primary", "secondary", "error", "warning", "info", "success"];
function channels(colour) {
  if (colour.startsWith("#")) {
    const hex = colour.length === 4 ? [1, 2, 3].map((i) => colour[i] + colour[i]).join("") : colour.slice(1, 7);
    const parsed = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
    if (parsed.some(Number.isNaN)) throw new Error(`focus ring: cannot read colour "${colour}"`);
    return parsed;
  }
  const numbers = (colour.match(/\d+(\.\d+)?/g) ?? []).slice(0, 3).map(Number);
  if (numbers.length < 3) throw new Error(`focus ring: cannot read colour "${colour}"`);
  return numbers;
}
function relativeLuminance(colour) {
  const [r, g, b] = channels(colour).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function contrastRatio(a, b) {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}
function chooseRingInk(preferred, surfaces) {
  if (surfaces.length === 0) {
    throw new Error("focus ring: chooseRingInk needs at least one surface to measure against");
  }
  const alternative = preferred.toLowerCase() === RING_INK_LIGHT ? RING_INK_DARK : RING_INK_LIGHT;
  const worst = (ink2) => surfaces.reduce(
    (lowest, surface) => {
      const ratio = contrastRatio(ink2, surface);
      return ratio < lowest.worstRatio ? { worstRatio: ratio, worstSurface: surface } : lowest;
    },
    { worstRatio: Number.POSITIVE_INFINITY, worstSurface: surfaces[0] }
  );
  const onPreferred = worst(preferred);
  const onAlternative = worst(alternative);
  const keepPreferred = onPreferred.worstRatio >= FOCUS_RING_CONTRAST_FLOOR || onAlternative.worstRatio < FOCUS_RING_CONTRAST_FLOOR && onPreferred.worstRatio >= onAlternative.worstRatio;
  const ink = keepPreferred ? preferred : alternative;
  const measured = keepPreferred ? onPreferred : onAlternative;
  return {
    ink,
    preferred,
    switched: ink !== preferred,
    worstRatio: measured.worstRatio,
    worstSurface: measured.worstSurface,
    clears: measured.worstRatio >= FOCUS_RING_CONTRAST_FLOOR
  };
}
function focusRingInkDecisions(mode, palette) {
  const { onSurface, onFill } = focusRingColors(mode);
  const fills = {};
  for (const slot of RING_PALETTE_SLOTS) {
    fills[slot] = chooseRingInk(onFill, [palette[slot].main, palette[slot].dark]);
  }
  return {
    surface: chooseRingInk(onSurface, [palette.background.paper, palette.background.default]),
    fills
  };
}
var capitalise = (slot) => slot.charAt(0).toUpperCase() + slot.slice(1);

export {
  FOCUS_RING_WIDTH_PX,
  focusRingColors,
  focusRing,
  focusVisibleComponents,
  FOCUS_RING_CONTRAST_FLOOR,
  RING_INK_DARK,
  RING_INK_LIGHT,
  RING_PALETTE_SLOTS,
  relativeLuminance,
  contrastRatio,
  chooseRingInk,
  focusRingInkDecisions
};
//# sourceMappingURL=chunk-GOUF5BMY.mjs.map